import {
  ISearchBoxState,
  ISearchboxOption,
  ISearchList,
  NavType
} from './types';
/**
 * Return the users controlled multiselections when a user has provided them
 * or the multiselections stored in SearchBox internal state
 */
export function readMultiSelections(
  { selectedMultiValues }: ISearchBoxState,
  controlledValue?: ISearchboxOption[]
): ISearchboxOption[] {
  return controlledValue || selectedMultiValues;
}
/**
 * Calculate a new list of selected MultiSelect values
 */
export function deriveNextMultiSelections(
  previousSelections: ISearchboxOption[],
  nextValue: ISearchboxOption
): ISearchboxOption[] {
  if (
    previousSelections &&
    previousSelections.some(
      (selectedOption: ISearchboxOption) =>
        selectedOption.value === nextValue.value
    )
  ) {
    return previousSelections.filter(
      (selectedOption: ISearchboxOption) =>
        selectedOption.value !== nextValue.value
    );
  }

  return [...previousSelections, nextValue];
}
/**
 * Determine the current keyboard navigated index
 */
export function deriveSelectionIndex(
  options: ISearchboxOption[],
  currentKeyboardValue: ISearchboxOption | null
): number {
  if (currentKeyboardValue !== null) {
    for (let i = 0; i < options.length; i += 1) {
      const option = options[i];
      if (option.value === currentKeyboardValue.value) {
        return i;
      }
    }
  }

  return -1;
}
/**
 * Predicate method determining if we should render selected
 * options in multiselect mode
 */
export function shouldRenderMultiSelections(
  multiSelections: ISearchboxOption[] | null
): boolean {
  return !!(multiSelections !== null && multiSelections.length);
}
/**
 * When in single select mode derive input value based on
 * interactionState,
 * controlled vs uncontrolled
 */
export function deriveSingleSelectValue(
  {
    interactionState,
    currentKeyboardValue,
    currentInputValue
  }: ISearchBoxState,
  controlledValue?: ISearchboxOption
): string {
  /**
   * When the user is acting or navigating we are
   * always displaying the internally tracked value regardless
   * of whether the component is controlled or not
   */
  if (interactionState === 'ACTING') {
    return currentInputValue;
  }

  if (interactionState === 'NAVIGATING') {
    return (
      (currentKeyboardValue && currentKeyboardValue.label) || currentInputValue
    );
  }
  /**
   * interactionState === IDLE
   * return controlled value or internally tracked value
   */
  return (controlledValue && controlledValue.label) || currentInputValue;
}
/**
 * When in multi select mode derive input value based on
 * interactionState,
 * controlled vs uncontrolled
 *
 * TODO: This logic is a little messy and complicated.
 * We should be able to simply at some point by refactoring our state management/actions.
 */
export function deriveMultiSelectValue(
  {
    interactionState,
    selectedMultiValues,
    currentInputValue,
    previousAction
  }: ISearchBoxState,
  delimiter: string,
  controlledValue?: ISearchboxOption[]
): string {
  /**
   * When the user is acting or navigating we are
   * always displaying the internally tracked value regardless
   * of whether the component is controlled or not
   */
  if (interactionState === 'ACTING' || interactionState === 'NAVIGATING') {
    return currentInputValue;
  }
  /*
   * If our previous action was clear, or just focused in multi-select mode,
   * or user made a selection: render the currentInputValue which should be '' ,
   */
  if (
    previousAction === 'CLEAR' ||
    previousAction === 'FOCUS_MULTI' ||
    previousAction === 'SELECT_MULTI_CLICK' ||
    previousAction === 'SELECT_MULTI_KEYBOARD'
  ) {
    return currentInputValue;
  }
  /**
   * If we get here:
   * interactionState is IDLE and we either blured or escaped.
   *
   * If we have a controlledValue render it,
   * if we have an internally tracked selectedValue and no controlledValue render it,
   * Otherwise, render the currentInputValue which in this case should be ''
   */
  return controlledValue
    ? controlledValue.map(s => s.label).join(`${delimiter} `) ||
        currentInputValue
    : selectedMultiValues.map(s => s.label).join(`${delimiter} `) ||
        currentInputValue;
}

/**
 * accumulateHeight
 * Sum the height of an array to the index specified, inclusive.
 * @param arr - the input array
 * @param index - the index where the operation ends, inclusive.
 * @return the summed height
 */
export function accumulateHeight(arr: number[], index: number): number {
  return arr.slice(0, index + 1).reduce((a, b) => a + b, 0);
}

/**
 * Compute the ScrollTop factor value. Top = 0, bottom = items.length - 1
 * @return the new scrollTop value.
 */
export function computeScrollTop(
  state: ISearchBoxState,
  payload: ISearchboxOption<ISearchList>
): number {
  let newScrollTop = state.currentScrollTop;
  const metadata = payload.metaData;

  if (!metadata) {
    return newScrollTop;
  }

  const { searchListRows, searchListHeight, type, index = 0 } = metadata;

  let listHeight = searchListHeight;

  // If the searchListRows exists, use the product of
  // the height of the first time and the searchListRows as the
  // list height.
  if (searchListRows) {
    listHeight = searchListRows * state.optionsHeight[0];
  }

  if (!listHeight) {
    return newScrollTop;
  }

  const accumulativeHeight = accumulateHeight(
    state.optionsHeight,
    index as number
  );

  switch (type) {
    case NavType.DOWN:
      // Only scroll down when the current selection is out of viewport.
      if (accumulativeHeight - state.currentScrollTop >= listHeight) {
        newScrollTop = accumulativeHeight - listHeight;
      }
      break;
    case NavType.UP:
      // Only scroll up when the current selection is out of viewport.
      if (
        accumulativeHeight - state.currentScrollTop <
        state.optionsHeight[index]
      ) {
        newScrollTop = accumulativeHeight - state.optionsHeight[index];
      }
      break;
    case NavType.BOTTOM:
      // Reset to bottom
      newScrollTop = accumulativeHeight - listHeight;
      break;
    case NavType.TOP:
      // Reset to top
      newScrollTop = 0;
      break;
    default:
      break;
  }

  return newScrollTop;
}

/**
 * Compute the scroll interval after the component is rendered.
 * Afterwards, the scroll interval stays the same.
 * Assuming each item has the same height.
 * @return the option box height
 */
export function computerOptionHeight(
  state: ISearchBoxState,
  payload: ISearchboxOption<ISearchList>
): number {
  const metadata = payload.metaData;

  if (!metadata) {
    return 0;
  }

  const { optionHeight, index } = metadata;

  if (index === undefined || index === -1) {
    return 0;
  }

  if (
    (state.optionsHeight[index] === 0 ||
      state.optionsHeight[index] === undefined) &&
    optionHeight > 0
  ) {
    return optionHeight;
  }

  return state.optionsHeight[index];
}

/**
 * If the searchListRows exists, use the product of
 * the height of the first time and the searchListRows as the
 * list height.
 * @return the search list client height
 */
export function deriveSearchListHeight(
  state: ISearchBoxState,
  payload: ISearchboxOption
): number | undefined {
  let height = state.searchListHeight;

  if (payload.metaData.searchListRows) {
    height = payload.metaData.searchListRows * state.optionsHeight[0];
  } else if (payload.metaData.searchListHeight) {
    height = payload.metaData.searchListHeight;
  }

  return height;
}

/**
 * deriveOptionsHeight
 * @return the array of option box height.
 */
export function deriveOptionsHeight(
  state: ISearchBoxState,
  payload: ISearchboxOption
): number[] {
  return [
    ...state.optionsHeight.slice(0, payload.metaData.index),
    computerOptionHeight(state, payload)
  ];
}
